/* eslint-disable @typescript-eslint/no-explicit-any */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import MediaRecorderOpusMedia from 'opus-media-recorder';
import MediaRecorderOpus from 'opus-recorder';

type ErrorType = {
  title: string;
  description: string;
};

type useAudioRecorderProps = {
  onStart?: () => void;
  onStop?: (mediaBlob: Blob) => void;
  deveUsarNovaLibDeAudio: boolean; // Feature flag
};

/** mimeType audio option */
const mimeType = 'audio/ogg';

/** opus-media-recorder options */
const workerOptions = {
  encoderWorkerFactory() {
    return new Worker(
      `${process.env.PUBLIC_URL}/opus-media-recorder/encoderWorker.umd.js`,
    );
  },
  OggOpusEncoderWasmPath: `${process.env.PUBLIC_URL}/opus-media-recorder/OggOpusEncoder.wasm`,
  WebMOpusEncoderWasmPath: `${process.env.PUBLIC_URL}/opus-media-recorder/WebMOpusEncoder.wasm`,
};

/** opus-recorder options */
const opusRecorderOptions = {
  encoderPath: '/opus-recorder/encoderWorker.min.js',
  mimeType,
};

export default function useAudioRecorder(props: useAudioRecorderProps) {
  const { onStart, onStop, deveUsarNovaLibDeAudio } = props;

  const mediaRecorderRef = useRef<any>(null); // MediaRecorder instance
  const streamRef = useRef<MediaStream | null>(null); // MediaStream

  const [error, setError] = useState<ErrorType | null>(null);
  const [mediaBlob, setMediaBlob] = useState<Blob | null>(null);
  const [recording, setRecording] = useState(false);
  const [time, setTime] = useState({ minutes: 0, seconds: 0 });
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const duration = useMemo(() => time.minutes * 60 + time.seconds, [time]);

  /** Starts the internal recording timer */
  const startTimer = useCallback(() => {
    if (timerRef.current) return;

    timerRef.current = setInterval(() => {
      setTime(prevTime => {
        const { minutes, seconds } = prevTime;
        if (seconds === 59) {
          return { minutes: minutes + 1, seconds: 0 };
        }
        return { minutes, seconds: seconds + 1 };
      });
    }, 1000);
  }, []);

  /** Clears and resets the internal recording timer */
  const resetTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setTime({ minutes: 0, seconds: 0 });
  }, []);

  /** Stop all the tracks on the active stream */
  const stopStream = useCallback(() => {
    if (!streamRef.current) return;

    streamRef.current.getTracks().forEach(track => track.stop());
    streamRef.current = null;
  }, []);

  /** Reset all the recording properties */
  const resetRecordingProperties = useCallback(() => {
    mediaRecorderRef.current = null;
    stopStream();
    setRecording(false);
    resetTimer();
  }, [resetTimer, stopStream]);

  /** Cancel audio recording */
  const cancel = useCallback(() => {
    if (!mediaRecorderRef.current) return;

    mediaRecorderRef.current.stop();
    resetRecordingProperties();
  }, [resetRecordingProperties]);

  const getCustomError = useCallback((error: Error): ErrorType => {
    switch (error.name) {
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return {
          title: `Microfone não encontrado`,
          description: `Você não pode gravar uma mensagem de voz porque parece que seu
            computador não tem um microfone. Tente conectar um microfone ou, caso
            já o tenha conectado, reinicie seu navegador.`,
        };
      case 'NotReadableError':
      case 'TrackStartError':
        return {
          title: `Microfone já está em uso`,
          description: `Você não pode gravar uma mensagem de voz porque parece que o
              microfone já está sendo utilizado. Verifique se alguma outra aplicação
              está utilizando o seu microfone no momento.`,
        };
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return {
          title: `Permitir microfone`,
          description: `Vá na sua barra de endereços, selecione o ícone referente as
              permissões de microfone e escolha "Sempre permitir que
              ${process.env.REACT_APP_HOMEPAGE_URL} tenha acesso ao seu microfone".`,
        };
      case 'OverconstrainedError':
      case 'ConstraintNotSatisfiedError':
        return {
          title: `Restrições encontradas`,
          description: `Parece que algumas restrições não podem ser satisfeitas pelo
              AVB de dispositivos`,
        };
      default:
        return {
          title: `Falha ao habilitar microfone`,
          description: `Não conseguimos habilitar o uso do seu microfone. Verifique se
              o seu navegador suporta este recurso e o seu computador possui e/ou não
              apresenta algum problema no microfone.`,
        };
    }
  }, []);

  /** Início da gravação para versão atual (opus-media-recorder) */
  const startRecordingCurrent = useCallback(() => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        streamRef.current = stream;
        window.MediaRecorder = MediaRecorderOpusMedia;

        const recorder = new MediaRecorderOpusMedia(
          stream,
          { mimeType },
          workerOptions,
        );

        mediaRecorderRef.current = recorder;

        setMediaBlob(null);

        recorder.addEventListener('dataavailable', (event: BlobEvent) => {
          const audioBlob = event.data;
          setMediaBlob(audioBlob);
          if (onStop) onStop(audioBlob);
        });

        recorder.addEventListener('start', () => {
          setRecording(true);
          startTimer();
          if (onStart) onStart();
        });

        recorder.start();
      })
      .catch((error: Error) => {
        stopStream();
        resetRecordingProperties();
        setError(getCustomError(error));
      });
  }, [
    getCustomError,
    onStart,
    onStop,
    resetRecordingProperties,
    startTimer,
    stopStream,
  ]);

  /** Início da gravação para nova versão (opus-recorder) */
  const startRecordingNew = useCallback(() => {
    navigator.mediaDevices
      .getUserMedia({ audio: true })
      .then(stream => {
        streamRef.current = stream;
        window.MediaRecorder = MediaRecorderOpus;

        const recorder = new MediaRecorderOpus(opusRecorderOptions);
        mediaRecorderRef.current = recorder;

        setMediaBlob(null);

        recorder.ondataavailable = (typedArray: Uint8Array) => {
          const audioBlob = new Blob([typedArray], { type: mimeType });
          setMediaBlob(audioBlob);
          if (onStop) onStop(audioBlob);
        };

        recorder.onstart = () => {
          setRecording(true);
          startTimer();
          if (onStart) onStart();
        };

        recorder.start(stream);
      })
      .catch((error: Error) => {
        stopStream();
        resetRecordingProperties();
        setError(getCustomError(error));
      });
  }, [
    getCustomError,
    onStart,
    onStop,
    resetRecordingProperties,
    startTimer,
    stopStream,
  ]);

  /** Inicia a gravação conforme a flag */
  const startRecording = useCallback(() => {
    if (!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia)) {
      setError({
        title: `Recurso não suportado`,
        description: `A API mediaDevices ou o método getUserMedia não são suportados neste navegador.`,
      });
      return;
    }

    if (deveUsarNovaLibDeAudio) {
      startRecordingNew();
    } else {
      startRecordingCurrent();
    }
  }, [deveUsarNovaLibDeAudio, startRecordingCurrent, startRecordingNew]);

  /** Para a gravação */
  const stopRecording = useCallback(() => {
    if (!mediaRecorderRef.current) return;
    cancel();
  }, [cancel]);

  /** Limpa recursos ao desmontar */
  useEffect(() => {
    return () => {
      if (mediaRecorderRef.current?.state === 'recording') {
        mediaRecorderRef.current.stop();
      }
      stopStream();
      resetTimer();
    };
  }, [resetTimer, stopStream]);

  return {
    startRecording,
    stopRecording,
    recording,
    minutes: time.minutes,
    seconds: time.seconds,
    duration,
    mediaBlob,
    error,
  };
}
